import type {
  EquationDocument,
  ReadonlyMatrixNode,
  ReadonlyVectorNode,
} from "./equation";
import { MAX_EQUATION_NODES, matrixDurationMs } from "./equation-policy";
import {
  draftMatrixValues,
  draftVectorValues,
  expressionDraft,
  matrixLabel,
  nodeId,
  vectorColor,
  vectorLabel,
} from "./equation-values";
import type { Dimension } from "@src/math/types";

export function parseEquationDocument<D extends Dimension>(
  value: unknown,
  expectedDimension: D,
): EquationDocument<D> {
  if (
    (expectedDimension !== 2 && expectedDimension !== 3) ||
    !isRecord(value) ||
    value.dimension !== expectedDimension
  )
    throw new Error("Invalid equation dimension");
  if (!Array.isArray(value.matrices) || !Array.isArray(value.vectors))
    throw new Error("Invalid equation collections");
  const rawMatrices: unknown[] = value.matrices;
  const rawVectors: unknown[] = value.vectors;
  if (
    rawMatrices.length > MAX_EQUATION_NODES ||
    rawVectors.length > MAX_EQUATION_NODES
  )
    throw new Error("Equation node capacity exceeded");
  const rawIds = rawMatrices.concat(rawVectors).map((item) => {
    if (!isRecord(item)) throw new Error("Invalid equation node ID");
    return nodeId(item.id);
  });
  if (new Set(rawIds).size !== rawIds.length)
    throw new Error("Duplicate equation node ID");

  const matrices = rawMatrices.map((matrix) =>
    parseMatrixNode(matrix, expectedDimension),
  );
  const vectors = rawVectors.map((vector) =>
    parseVectorNode(vector, expectedDimension),
  );
  assertUniqueLabels(matrices);
  assertUniqueLabels(vectors);

  return {
    dimension: expectedDimension,
    matrices,
    vectors,
  };
}

export function copyEquationDocument<D extends Dimension>(
  document: EquationDocument<D>,
): EquationDocument<D> {
  return {
    dimension: document.dimension,
    matrices: document.matrices.map((matrix) => ({
      ...matrix,
      entries: draftMatrixValues(document.dimension, matrix.entries),
    })),
    vectors: document.vectors.map((vector) => ({
      ...vector,
      coordinates: draftVectorValues(document.dimension, vector.coordinates),
    })),
  };
}

function assertUniqueLabels(
  items: readonly { readonly label: string }[],
): void {
  if (new Set(items.map(({ label }) => label)).size !== items.length)
    throw new Error("Duplicate equation node label");
}

export function parseAnyEquationDocument(
  value: unknown,
): EquationDocument<2> | EquationDocument<3> {
  if (!isRecord(value)) throw new Error("Invalid equation document");
  if (value.dimension === 2) return parseEquationDocument(value, 2);
  if (value.dimension === 3) return parseEquationDocument(value, 3);
  throw new Error("Invalid equation dimension");
}

function parseMatrixNode<D extends Dimension>(
  value: unknown,
  dimension: D,
): ReadonlyMatrixNode<D> {
  if (!isRecord(value) || !Array.isArray(value.entries))
    throw new Error("Invalid matrix node");
  const id = nodeId(value.id);
  if (value.entries.length !== dimension ** 2)
    throw new Error(`Invalid matrix entry count: ${id}`);
  if (!value.entries.every((entry) => typeof entry === "string"))
    throw new Error(`Invalid equation entry type: ${id}`);
  const entries = draftMatrixValues(
    dimension,
    value.entries.map(expressionDraft),
  );
  return {
    id,
    label: matrixLabel(value.label),
    entries,
    durationMs: matrixDurationMs(requireNumber(value.durationMs)),
  };
}

function parseVectorNode<D extends Dimension>(
  value: unknown,
  dimension: D,
): ReadonlyVectorNode<D> {
  if (!isRecord(value) || !Array.isArray(value.coordinates))
    throw new Error("Invalid vector node");
  const id = nodeId(value.id);
  if (value.coordinates.length !== dimension)
    throw new Error(`Invalid vector coordinate count: ${id}`);
  if (!value.coordinates.every((entry) => typeof entry === "string"))
    throw new Error(`Invalid equation entry type: ${id}`);
  const coordinates = draftVectorValues(
    dimension,
    value.coordinates.map(expressionDraft),
  );
  return {
    id,
    label: vectorLabel(value.label),
    coordinates,
    color: vectorColor(value.color),
  };
}

function requireNumber(value: unknown): number {
  if (typeof value !== "number") throw new Error("Invalid numeric value");
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
